import { useQuery, useMutation } from "@apollo/client/react"
import { CircularProgress, Alert, IconButton, Button } from "@mui/material"
import { Formik, FormikHelpers } from "formik"
import { X } from "lucide-mui"
import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"

import {
  BookingPolicyPreview,
  BookingWindow,
  ReservationTiming,
  NoShowPolicy,
  LargeGroups,
} from "@/components/settings/reservation_rules"
import { MINUTE } from "@/constants/reservation_rules"
import {
  GetVenueReservationConfigurationDocument,
  type GetVenueReservationConfigurationQuery,
  type GetVenueReservationConfigurationQueryVariables,
  type UpdateReservationConfigurationMutationVariables,
  UpdateReservationConfigurationDocument,
  type UpdateReservationConfigurationMutation,
} from "@/graphql/graphql"
import i18n from "@/i18n"
import {
  buildBookingPolicyTileOrder,
  buildBookingPolicyTiles,
  getAllBookingPolicyTileKinds,
  type BookingPolicyTileKind,
} from "@/shared/booking_policy"
import { usePanelLink } from "@/shared/hooks"
import { showErrorToast } from "@/shared/toast"

interface ReservationRulesFormValues {
  bookingPolicyTiles: BookingPolicyTileKind[]
  maxAdvanceBooking: number
  minAdvanceBooking: number
  minReservationTime: number
  maxReservationTime: number
  reservationBuffer: number
  autoCancelAfter: number | null
  cancellationDeadline: number
  largeGroupThreshold: number
}

const ReservationRules: React.FC = () => {
  const { venueSlug } = useParams()
  const navigate = useNavigate()
  const linkTo = usePanelLink()
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [bookingPolicyOrder, setBookingPolicyOrder] = useState<BookingPolicyTileKind[]>([])

  const { data, loading, error } = useQuery<
    GetVenueReservationConfigurationQuery,
    GetVenueReservationConfigurationQueryVariables
  >(GetVenueReservationConfigurationDocument, {
    variables: { slug: venueSlug },
  })

  const [updateReservationConfigurationMutation, { loading: savingConfig }] = useMutation<
    UpdateReservationConfigurationMutation,
    UpdateReservationConfigurationMutationVariables
  >(UpdateReservationConfigurationDocument)
  const saving = savingConfig

  const venue = data?.venue
  const config = venue?.reservationConfiguration
  const availableBookingPolicyTiles =
    (config?.availableBookingPolicyTiles as BookingPolicyTileKind[] | null) || getAllBookingPolicyTileKinds()

  useEffect(() => {
    setBookingPolicyOrder(
      buildBookingPolicyTileOrder(
        availableBookingPolicyTiles,
        config?.bookingPolicyTiles as BookingPolicyTileKind[] | null,
      ),
    )
  }, [availableBookingPolicyTiles, config?.bookingPolicyTiles])

  useEffect(() => {
    if (venue && !venue.permissions?.manageVenue) {
      navigate(linkTo("settings"))
    }
  }, [venue?.permissions?.manageVenue, navigate, linkTo])

  const handleSubmit = async (
    values: ReservationRulesFormValues,
    { setSubmitting }: FormikHelpers<ReservationRulesFormValues>,
  ) => {
    if (!venue) return

    setSuccessMessage(null)
    setErrorMessage(null)

    try {
      // Save configuration
      const configResult = await updateReservationConfigurationMutation({
        variables: {
          venueId: venue.id,
          maxAdvanceBookingTime: values.maxAdvanceBooking,
          minAdvanceBookingTime: values.minAdvanceBooking,
          minReservationTime: values.minReservationTime,
          maxReservationTime: values.maxReservationTime,
          reservationBuffer: values.reservationBuffer,
          noshowAutoCancelAfter: values.autoCancelAfter,
          cancellationDeadline: values.cancellationDeadline,
          largeGroupThreshold: values.largeGroupThreshold,
          bookingPolicyTiles:
            values.bookingPolicyTiles as UpdateReservationConfigurationMutationVariables["bookingPolicyTiles"],
        },
      })

      if (!configResult.data?.updateReservationConfiguration?.success) {
        const errors = configResult.data?.updateReservationConfiguration?.errors
        const errorCode = errors?.[0]?.code
        const errorKey = errorCode
          ? `app.settings.venues.tabbio.errors.${errorCode.toLowerCase()}`
          : "app.settings.venues.tabbio.errors.unknown"
        setErrorMessage(i18n.t(errorKey))
        return
      }

      setSuccessMessage(i18n.t("app.settings.venues.tabbio.success"))
    } catch (err) {
      showErrorToast()
      console.error("Error saving reservation rules:", err)
      setErrorMessage(i18n.t("app.settings.venues.tabbio.errors.generic"))
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return <div className="text-red-500 p-8">Error loading venue: {error.message}</div>
  }

  if (!venue) {
    return <div className="p-8">Venue not found</div>
  }

  // Map GraphQL data to form values
  const initialValues: ReservationRulesFormValues = {
    bookingPolicyTiles: (config?.bookingPolicyTiles as BookingPolicyTileKind[] | null) || availableBookingPolicyTiles,
    maxAdvanceBooking: config?.maxAdvanceBookingTime || 0,
    minAdvanceBooking: config?.minAdvanceBookingTime || 0,
    minReservationTime: config?.minReservationTime || 0,
    maxReservationTime: config?.maxReservationTime || 0,
    reservationBuffer: config?.reservationBuffer || 0,
    autoCancelAfter: config?.noshowAutoCancelAfter ?? 30 * MINUTE,
    cancellationDeadline: config?.cancellationDeadline || 0,
    largeGroupThreshold: config?.largeGroupThreshold || 8,
  }

  const hasChanges = (formik: any) => formik.dirty

  return (
    <Formik initialValues={initialValues} enableReinitialize onSubmit={handleSubmit}>
      {(formik) => (
        <div className="flex max-w-4xl flex-col gap-6">
          <h1 className="mb-0 text-3xl font-bold" data-testid="settings-venue-rules-title">
            {i18n.t("app.settings.venues.reservation_rules.title")}
          </h1>
          <p className="mt-0 text-sm text-gray-500">{i18n.t("app.settings.venues.reservation_rules.subtitle")}</p>

          {!!successMessage && (
            <Alert
              severity="success"
              data-testid="settings-venue-rules-alert-success"
              action={
                <IconButton aria-label="close" color="inherit" size="small" onClick={() => setSuccessMessage(null)}>
                  <X className="w-5 h-5" />
                </IconButton>
              }
            >
              {successMessage}
            </Alert>
          )}

          {!!errorMessage && (
            <Alert
              severity="error"
              data-testid="settings-venue-rules-alert-error"
              action={
                <IconButton aria-label="close" color="inherit" size="small" onClick={() => setErrorMessage(null)}>
                  <X className="w-5 h-5" />
                </IconButton>
              }
            >
              {errorMessage}
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit} className="flex flex-col gap-6" data-testid="settings-venue-rules-form">
            <BookingPolicyPreview
              tiles={buildBookingPolicyTiles(
                {
                  availableBookingPolicyTiles,
                  bookingPolicyTiles: formik.values.bookingPolicyTiles,
                  minReservationTime: formik.values.minReservationTime,
                  maxReservationTime: formik.values.maxReservationTime,
                  noshowAutoCancelAfter: formik.values.autoCancelAfter,
                  largeGroupThreshold: formik.values.largeGroupThreshold,
                  cancellationDeadline: formik.values.cancellationDeadline,
                  phone: venue.info?.phone,
                  email: venue.info?.email,
                },
                {
                  includeDisabled: true,
                  includeUnconfigured: true,
                  orderedKinds: bookingPolicyOrder,
                },
              )}
              sectionTitle={
                venue.reservationRuleSections?.find((section) => section.identifier === "booking_policy")?.title
              }
              content={
                venue.reservationRuleSections?.find((section) => section.identifier === "booking_policy")?.content
              }
              onChange={(tiles) => {
                setBookingPolicyOrder(tiles.map((tile) => tile.kind))
                void formik.setFieldValue(
                  "bookingPolicyTiles",
                  tiles.filter((tile) => tile.enabled).map((tile) => tile.kind),
                )
              }}
            />
            <BookingWindow />
            <ReservationTiming />
            <NoShowPolicy />
            <LargeGroups />

            <div className="flex justify-end gap-3">
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  formik.resetForm()
                  setBookingPolicyOrder(
                    buildBookingPolicyTileOrder(
                      availableBookingPolicyTiles,
                      config?.bookingPolicyTiles as BookingPolicyTileKind[] | null,
                    ),
                  )
                }}
                disabled={saving || !hasChanges(formik)}
                data-testid="settings-venue-rules-btn-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={saving || !formik.isValid}
                data-testid="settings-venue-rules-btn-save"
              >
                {saving ? <CircularProgress size={24} /> : "Save Changes"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </Formik>
  )
}

export default ReservationRules
